import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { http, formatApiErrorDetail, setActiveTenantHeader } from "@/lib/api";
import { can as canScope } from "@/lib/access";
import { setDisplayTimezone, setDisplayCurrency } from "@/lib/format";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Backward-compatible permission check.
// Accepts exact perms ("dashboard.view") and "resource.action" (resolves any scope).
export const hasPerm = (user, perm) => {
  if (!user) return false;
  const perms = user.permissions || [];
  if (perms.includes("*") || perms.includes(perm)) return true;
  const parts = perm.split(".");
  if (parts.length === 2) return canScope(user, parts[0], parts[1]);
  return false;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [meta, setMeta] = useState({ roles: [], permissions: [], permission_groups: [] });
  const [access, setAccess] = useState(null);
  const [activeTenant, setActiveTenantState] = useState(() => localStorage.getItem("activeTenant") || null);

  // keep axios header in sync
  useEffect(() => { setActiveTenantHeader(activeTenant); }, [activeTenant]);

  // keep global date formatting in sync: personal override -> browser timezone -> Europe/Kyiv
  useEffect(() => { setDisplayTimezone(user?.timezone); }, [user]);

  const setActiveTenant = useCallback((tid) => {
    setActiveTenantHeader(tid);
    if (tid) localStorage.setItem("activeTenant", tid);
    else localStorage.removeItem("activeTenant");
    setActiveTenantState(tid || null);
  }, []);

  const qc = useQueryClient();
  const loadAccess = useCallback(async (u) => {
    // Auth state changed → drop the anonymous-cached tenant branding so the buyer's
    // network name/favicon applies immediately after login (not after the next refetch).
    qc.invalidateQueries({ queryKey: ["my-branding"] });
    if (u && ["manager", "buyer"].includes(u.role)) {
      try { setAccess((await http.get("/tenant-settings/my-access")).data?.access || null); }
      catch { setAccess(null); }
    } else { setAccess(null); }
  }, [qc]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await http.get("/auth/me");
      setUser(data);
      await loadAccess(data);
    } catch {
      setUser(false);
      setAccess(null);
    }
  }, [loadAccess]);

  useEffect(() => {
    setActiveTenantHeader(activeTenant);
    refresh();
    http.get("/auth/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, [refresh]); // eslint-disable-line

  const login = async (email, password, remember = false) => {
    try {
      const { data } = await http.post("/auth/login", { email, password, remember: !!remember });
      if (data?.status === "2fa_required") {
        return { ok: false, twofa: true, challenge: data.challenge, methods: data.methods || ["totp", "recovery"] };
      }
      setUser(data);
      await loadAccess(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const portalLogin = async (email, password, slug, remember = false) => {
    try {
      const { data } = await http.post("/public/login", { email, password, slug: slug || null, remember: !!remember });
      if (data?.status === "2fa_required") {
        return { ok: false, twofa: true, challenge: data.challenge, methods: data.methods || ["totp", "recovery"] };
      }
      // A Hold account is not logged in — it always gets the waiting screen (manager contacts).
      if (data?.status === "hold") {
        return { ok: false, hold: true, result: data };
      }
      setUser(data);
      await loadAccess(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const verify2fa = async (challenge, { code, recovery_code, trust_device, remember }) => {
    try {
      const { data } = await http.post("/auth/2fa/verify", { challenge, code: code || null, recovery_code: recovery_code || null, trust_device: !!trust_device, remember: !!remember });
      setUser(data);
      await loadAccess(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const devLogin = async (role) => {
    // Dev-login is a development/preview-only convenience. It is compiled out of production
    // builds so no dev-login flow is reachable in production.
    if (process.env.NODE_ENV === "production") {
      throw new Error("Developer login is disabled in production");
    }
    const { data } = await http.post("/auth/dev-login", { role });
    setActiveTenant(null);
    setUser(data);
    await loadAccess(data);
    return data;
  };

  const logout = async () => {
    await http.post("/auth/logout").catch(() => {});
    setActiveTenant(null);
    setUser(false);
    qc.invalidateQueries({ queryKey: ["my-branding"] });
  };

  const can = useCallback((resource, action) => canScope(user, resource, action), [user]);

  const impersonate = useCallback(async (tenantId, role = "tenant_admin") => {
    await http.post("/auth/impersonate", { tenant_id: tenantId, role });
    if (tenantId) localStorage.setItem("impersonationOrigin", tenantId);
    // Full reload so the whole app (data, permissions, menus, routing) switches context at once.
    window.location.assign("/");
  }, []);

  const stopImpersonate = useCallback(async () => {
    await http.post("/auth/stop-impersonate");
    window.location.assign("/");
  }, []);

  const impersonateUser = useCallback(async (userId) => {
    await http.post("/auth/impersonate-user", { user_id: userId });
    // Hard reload → the impersonated user's Dashboard opens immediately, no manual refresh.
    window.location.assign("/");
  }, []);

  return (
    <AuthContext.Provider value={{ user, meta, access, activeTenant, setActiveTenant, login, portalLogin, verify2fa, devLogin, logout, refresh, can, impersonate, impersonateUser, stopImpersonate }}>
      {children}
    </AuthContext.Provider>
  );
}
